// Лента уведомлений (T051; contracts/ui.md, «Уведомления в 001»; ux.md, раздел 9): по дням —
// «Сегодня», «Вчера», «На этой неделе», «Раньше»; клик отмечает прочитанным и ведёт к записи.
// Число непрочитанных — на колокольчике: фоновый опрос раз в минуту с X-KP-Background, он не
// продлевает сеанс (FR-008б). Открыть ленту — действие человека, её запрос обычный.
// На широком экране лента — панель под колокольчиком, до 640 px — окно снизу на весь экран.
// Уведомление о сбое (002) — под текстом строка «Код … · операция …» с «Скопировать номер»
// (FR-028) и кнопка действия: «Заказать снова» ведёт в «Мои выгрузки» на заказ (FR-053а).
// Переход из уведомления перечитывает список «Моих выгрузок»: заказ, о котором оно, мог
// измениться с прошлого показа — и на уже открытой странице тоже (T119). Пометка «прочитано»
// без тоста об успехе, а её сбой — тост-ошибка с «Повторить» (ux.md, раздел 2).
//
// 031 (T025; FR-004, FR-007; ASG-HIST-01, ASG-HIST-02; ADR-0046): уведомления о пунктах — строки
// с номерами протокола и пункта. Текст строки — заголовок по каталогу событий, его строит сервер
// («Назначили: пункт 12, П-14», «Вам поручено 5 пунктов в П-14»). Переход строится из номеров:
// /protocols/{протокол}?item={пункт}, у сводной строки — таблица протокола. «Требует действия» —
// пометка у строки словами. Протокол пользователю больше не виден (canOpen: false — фильтр
// видимости при чтении) — строка остаётся, но без перехода: не кнопка, под текстом «Протокол вам
// больше не виден»; непрочитанную отмечают кнопкой «Отметить прочитанным».
//
// 032 (T026; FR-002, FR-003; ASG-HIST-03, ASG-HIST-04): колокольчик — короткий вид «Входящих»: та же
// лента, внизу — «Все во Входящих». Число у колокольчика — непрочитанные «требует действия»
// (actionUnread), как у «Входящих» в меню; тот же фоновый опрос даёт число меню и нижней навигации —
// второго опроса нет. Требующих действия нет, а непрочитанные есть — у колокольчика точка без числа.
//
// 0.26.1 (ACC-06): смысл числа прежний, подпись однозначная — имя и подсказка колокольчика
// «Уведомления, непрочитанные, требуют действия: 3»: прочитанные, но не разобранные строки ждут
// в «Нерешённых» «Входящих». У строки — состояние разбора словами (features/inbox/action-state.tsx):
// «Требует действия», «Прочитано, нужен разбор», «Внесено», «Закрыто», «Вопрос снят». Ревью 0.26.1
// (W3): число не считает разобранные строки — их разобрал другой контролёр или вопрос снят сам.
import type { Notification, NotificationAction } from '@kp/contracts';
import {
  Button,
  Drawer,
  EmptyState,
  OperationCode,
  Popover,
  Skeleton,
  SkeletonRow,
  cx,
} from '@kp/ui';
import { BellOff, CheckCheck } from '@kp/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { api, apiKey, apiQuery } from '../api/client.ts';
import { errorView, useErrorToast } from '../app/errors.ts';
import { dateIn, formatDateTime, formatTime, shiftDate } from '../app/format.ts';
import { safeNext } from '../features/auth/safe-next.ts';
import { ActionMark } from '../features/inbox/action-state.tsx';
import { useShell } from './context.tsx';
import { INBOX_COUNTER_SPOKEN, INBOX_PATH } from './permissions.ts';
import { useTimeZone } from './session.ts';
import './notifications.css';

/**
 * Счётчики ленты: фоновый опрос раз в минуту, сеанс не продлевает (FR-008б). Один опрос на
 * колокольчик, счётчик «Входящих» в меню и нижнюю навигацию (032).
 */
export function notificationsQuery() {
  return {
    ...apiQuery('notifications.list', { query: { unread: 'true' }, background: true }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  };
}

/** Число у колокольчика и «Входящих»: action — «требует действия», unread — все непрочитанные. */
export interface InboxCounts {
  action: number;
  unread: number;
}

/**
 * Счётчики из фонового опроса (032: FR-003, SC-005). enabled: false — без запроса, нули: пункта
 * со счётчиком на экране нет.
 */
export function useInboxCounts(enabled = true): InboxCounts {
  const counts = useQuery({
    ...notificationsQuery(),
    enabled,
    select: (data) => ({ action: data.actionUnread, unread: data.unreadCount }),
  }).data;
  return { action: counts?.action ?? 0, unread: counts?.unread ?? 0 };
}

/**
 * Имя колокольчика для программ чтения и подсказка: «Уведомления, непрочитанные, требуют действия:
 * 3» (0.26.1, ACC-06); таких нет, а непрочитанные есть — «Уведомления, непрочитанных: 5».
 */
export function bellLabel({ action, unread }: InboxCounts): string {
  if (action > 0) return `Уведомления, ${INBOX_COUNTER_SPOKEN}: ${action}`;
  return unread > 0 ? `Уведомления, непрочитанных: ${unread}` : 'Уведомления';
}

/**
 * Число у колокольчика — «требует действия» (99+); их нет, а непрочитанные есть — точка без числа.
 * Ничего непрочитанного — ничего.
 */
export function BellCount({ counts }: { counts: InboxCounts }) {
  if (counts.action > 0) {
    return (
      <span className="kp-bell-count t-num" aria-hidden="true">
        {counts.action > 99 ? '99+' : counts.action}
      </span>
    );
  }
  return counts.unread > 0 ? <span className="kp-bell-count is-dot" aria-hidden="true" /> : null;
}

export interface DayGroup {
  title: 'Сегодня' | 'Вчера' | 'На этой неделе' | 'Раньше';
  items: Notification[];
}

/** Уведомления по дням в поясе установки; пустые группы не показываются. */
export function groupByDay(
  items: readonly Notification[],
  timeZone: string,
  now: Date = new Date(),
): DayGroup[] {
  const today = dateIn(timeZone, now);
  const yesterday = shiftDate(today, -1);
  // Понедельник этой недели: getUTCDay() у полудня даты — день недели без сдвига пояса.
  const weekday = new Date(`${today}T12:00:00Z`).getUTCDay();
  const monday = shiftDate(today, -((weekday + 6) % 7));
  const groups: DayGroup[] = [
    { title: 'Сегодня', items: [] },
    { title: 'Вчера', items: [] },
    { title: 'На этой неделе', items: [] },
    { title: 'Раньше', items: [] },
  ];
  const sorted = [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  for (const item of sorted) {
    const day = dateIn(timeZone, new Date(item.createdAt));
    const index = day >= today ? 0 : day === yesterday ? 1 : day >= monday ? 2 : 3;
    groups[index]?.items.push(item);
  }
  return groups.filter((group) => group.items.length > 0);
}

/**
 * Под строкой уведомления о сбое: «Код … · операция …» с «Скопировать номер» и кнопка действия.
 * Кнопки внутри кнопки строки быть не может — поэтому они под ней. У прочих уведомлений — ничего.
 */
function FailureLine({
  item,
  onAct,
}: {
  item: Notification;
  onAct: (action: NotificationAction) => void;
}) {
  const { code, operation, action } = item;
  if (!(code && operation) && !action) return null;
  return (
    <div className="kp-ntf-extra">
      {code && operation ? <OperationCode code={code} operation={operation} /> : null}
      {action ? (
        <Button size="sm" onClick={() => onAct(action)}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Свой переход строки не в протокол (032, T049; FR-021): «Ответ получен» руководителю ведёт на
 * /team. Фильтр видимости протокола такой переход не снимает: протокол руководителю не виден,
 * а страница «Просрочено у ваших сотрудников» — его.
 */
function ownLink(item: Notification): string | undefined {
  const { link } = item;
  if (!link) return undefined;
  const path = link.split(/[?#]/, 1)[0] ?? '';
  return path === '/protocols' || path.startsWith('/protocols/') ? undefined : link;
}

/**
 * Переход уведомления (031, FR-004): у уведомления о пункте — из номеров протокола и пункта,
 * у сводной строки — таблица протокола; протокол больше не виден — перехода нет. У прочих —
 * ссылка уведомления; у строки о пункте со своим переходом не в протокол — он (032, T049).
 */
export function noticeLink(item: Notification): string | undefined {
  if (!item.documentId) return item.link;
  const own = ownLink(item);
  if (own) return own;
  if (item.canOpen === false) return undefined;
  const protocol = `/protocols/${encodeURIComponent(item.documentId)}`;
  return item.itemId ? `${protocol}?item=${encodeURIComponent(item.itemId)}` : protocol;
}

/**
 * Уведомление о пункте протокола, который пользователю больше не виден (FR-007). Строка со своим
 * переходом не в протокол («Ответ получен» руководителю) — не такая: протокол ему и не открывался.
 */
export function isHiddenNotice(item: Notification): boolean {
  return item.documentId !== undefined && item.canOpen === false && ownLink(item) === undefined;
}

/** Пояснение у строки без перехода. */
export const HIDDEN_NOTICE_TEXT = 'Протокол вам больше не виден';

function feedQuery() {
  return apiQuery('notifications.list');
}

/**
 * Что перечитать при переходе из уведомления: страница уже могла быть открыта, а её список
 * в кэше — моложе 30 секунд, и заказ в нём ещё «Готовится» (T119).
 */
const refreshOnOpen: Readonly<Record<string, readonly unknown[]>> = {
  '/audit/exports': apiKey('audit.exports.list'),
};

/** Содержимое ленты: шапка с «Прочитать все», группы по дням, пусто, загрузка, ошибка. */
function FeedContent({ onNavigate, showTitle }: { onNavigate: () => void; showTitle: boolean }) {
  const timeZone = useTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showError = useErrorToast();
  const feed = useQuery(feedQuery());

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['api', 'notifications.list'] });

  /**
   * Пометка «прочитано» — без тоста: итог виден на месте (ux.md, раздел 2). Не сохранилась —
   * тост-ошибка с «Повторить»: лента уже закрылась, точка непрочитанного осталась бы молча.
   */
  const markRead = (id: string) => {
    api('notifications.read', { params: { id } })
      .then(refresh)
      .catch((error: unknown) => showError(error, () => markRead(id)));
  };

  /** Отмечает прочитанным и ведёт по ссылке уведомления или его кнопки. */
  const go = (item: Notification, link: string | undefined) => {
    if (!item.readAt) markRead(item.id);
    onNavigate();
    // Ведём только внутри сервиса.
    if (!link || safeNext(link) !== link) return;
    const stale = refreshOnOpen[link.split(/[?#]/, 1)[0] ?? ''];
    if (stale) void queryClient.invalidateQueries({ queryKey: stale });
    void navigate({ href: link });
  };
  const open = (item: Notification) => go(item, noticeLink(item));
  const act = (item: Notification, action: NotificationAction) => go(item, action.link);

  const readAll = async () => {
    try {
      await api('notifications.readAll');
    } catch (error) {
      showError(error, () => void readAll());
      return;
    }
    await refresh();
  };

  let body: ReactElement;
  if (feed.data) {
    const groups = groupByDay(feed.data.items, timeZone);
    body =
      groups.length === 0 ? (
        <EmptyState icon={BellOff} title="Уведомлений нет" headingLevel={3} />
      ) : (
        <div className="kp-ntf-groups">
          {groups.map((group) => (
            <section key={group.title} aria-label={group.title}>
              <h3 className="kp-ntf-day">{group.title}</h3>
              <ul className="kp-ntf-list">
                {group.items.map((item) => {
                  const hidden = isHiddenNotice(item);
                  const content = (
                    <>
                      <span className="kp-ntf-dot" aria-hidden="true" />
                      <span className="kp-ntf-text">
                        {!item.readAt ? <span className="kp-sr-only">Не прочитано: </span> : null}
                        <span className="kp-ntf-title">{item.title}</span>
                        {item.body ? <span className="kp-ntf-body">{item.body}</span> : null}
                        {item.actionRequired || hidden ? (
                          <span className="kp-ntf-marks">
                            <ActionMark item={item} />
                            {hidden ? (
                              <span className="kp-ntf-body">{HIDDEN_NOTICE_TEXT}</span>
                            ) : null}
                          </span>
                        ) : null}
                      </span>
                      <time className="kp-ntf-time t-num" dateTime={item.createdAt}>
                        {group.title === 'Сегодня' || group.title === 'Вчера'
                          ? formatTime(item.createdAt, timeZone)
                          : formatDateTime(item.createdAt, timeZone)}
                      </time>
                    </>
                  );
                  return (
                    <li key={item.id}>
                      {hidden ? (
                        // Перехода нет — строка не кнопка (FR-007).
                        <div className={cx('kp-ntf is-static', !item.readAt && 'is-unread')}>
                          {content}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={cx('kp-ntf', !item.readAt && 'is-unread')}
                          onClick={() => open(item)}
                        >
                          {content}
                        </button>
                      )}
                      {hidden && !item.readAt ? (
                        <div className="kp-ntf-extra">
                          <Button size="sm" variant="ghost" onClick={() => markRead(item.id)}>
                            Отметить прочитанным
                          </Button>
                        </div>
                      ) : null}
                      <FailureLine item={item} onAct={(action) => act(item, action)} />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      );
  } else if (feed.error) {
    const view = errorView(feed.error);
    body = (
      <EmptyState
        error
        headingLevel={3}
        title="Не удалось загрузить"
        text={`${view.message}. ${view.nextStep}`}
        code={view.code}
        operation={view.operation}
        action={
          // Лента — слой поверх экрана со своей кнопкой-акцентом: «Повторить» — вторичная, чтобы
          // на экране не было второй кнопки-акцента (README «Цвет»).
          <Button onClick={() => void feed.refetch()}>Повторить</Button>
        }
      />
    );
  } else {
    body = (
      <Skeleton minHeight={160}>
        <SkeletonRow width="70%" />
        <SkeletonRow width="55%" />
        <SkeletonRow width="64%" />
      </Skeleton>
    );
  }

  const unread = feed.data?.unreadCount ?? 0;
  return (
    <div className="kp-ntf-feed">
      {showTitle || unread > 0 ? (
        <div className="kp-ntf-head">
          {showTitle ? <h2>Уведомления</h2> : null}
          <span className="sp" />
          {unread > 0 ? (
            <Button size="sm" variant="ghost" icon={CheckCheck} onClick={() => void readAll()}>
              Прочитать все
            </Button>
          ) : null}
        </div>
      ) : null}
      {body}
      {/* 032 (FR-002): колокольчик — короткий вид; вся лента с вкладками — «Входящие». */}
      <div className="kp-ntf-foot">
        <Link to={INBOX_PATH} className="kp-link" onClick={onNavigate}>
          Все во Входящих
        </Link>
      </div>
    </div>
  );
}

/** Панель под колокольчиком шапки (шире 640 px). */
export function NotificationsPopover({ trigger }: { trigger: ReactElement }) {
  const { notificationsOpen, setNotificationsOpen } = useShell();
  return (
    <Popover
      trigger={trigger}
      label="Уведомления"
      open={notificationsOpen}
      onOpenChange={setNotificationsOpen}
      align="end"
      className="kp-ntf-pop"
    >
      <FeedContent showTitle onNavigate={() => setNotificationsOpen(false)} />
    </Popover>
  );
}

/**
 * До 640 px: окно снизу на весь экран — колокольчик шапки сеанса по ссылке. «Уведомления» нижней
 * навигации с 032 ведут во «Входящие».
 */
export function NotificationsSheet() {
  const { notificationsOpen, setNotificationsOpen } = useShell();
  if (!notificationsOpen) return null;
  return (
    <Drawer open onOpenChange={setNotificationsOpen} title="Уведомления">
      <FeedContent showTitle={false} onNavigate={() => setNotificationsOpen(false)} />
    </Drawer>
  );
}
